import React, { useState, useEffect } from 'react';
import { Users, UserPlus, X, Shield, Loader2, RefreshCw, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SubcaseMembers Component
 * Manages members for a subcase with inheritance toggle
 * 
 * Props:
 *   - subcaseId: Subcase UUID
 *   - caseId: Parent case UUID
 *   - isLead: Whether current user can manage members
 */
const SubcaseMembers = ({ subcaseId, caseId, isLead }) => {
  const [members, setMembers] = useState([]);
  const [inheritMembers, setInheritMembers] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableLawyers, setAvailableLawyers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('ASSISTANT');

  const roles = [
    { value: 'ASSISTANT', label: 'Medarbeider' },
    { value: 'PARALEGAL', label: 'Paralegal' },
    { value: 'EXPERT', label: 'Ekspert' },
    { value: 'EXTERNAL', label: 'Ekstern' }
  ];

  useEffect(() => {
    if (subcaseId) {
      fetchMembers();
    }
  }, [subcaseId]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/subcases/${subcaseId}/members`);
      setMembers(response.data.members || []);
      setInheritMembers(response.data.inherit_members);
    } catch (error) {
      console.error('Failed to fetch subcase members:', error);
      toast.error('Kunne ikke laste medlemmer');
    } finally {
      setLoading(false);
    }
  };

  const toggleInheritance = async () => {
    try {
      const newValue = !inheritMembers;
      await axios.patch(`${API}/subcases/${subcaseId}/settings`, {
        inherit_members: newValue
      });
      
      setInheritMembers(newValue);
      toast.success(newValue ? 'Arver nå medlemmer fra hovedsak' : 'Bruker nå egendefinerte medlemmer');
      fetchMembers();
    } catch (error) {
      console.error('Failed to toggle inheritance:', error);
      toast.error('Kunne ikke endre innstilling');
    }
  };

  const fetchAvailableLawyers = async () => {
    try {
      // Get all tenant lawyers
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/case-members/tenant-lawyers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out existing members
      const existingIds = members.map(m => m.user_id);
      const available = response.data.filter(l => !existingIds.includes(l.id));
      
      setAvailableLawyers(available);
    } catch (error) {
      console.error('Failed to fetch lawyers:', error);
      toast.error('Kunne ikke laste advokater');
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId) {
      toast.error('Velg et medlem');
      return;
    }

    try {
      await axios.post(`${API}/subcases/${subcaseId}/members`, {
        user_id: selectedUserId,
        role: selectedRole
      });
      
      toast.success('Medlem lagt til');
      setShowAddModal(false);
      setSelectedUserId('');
      setSelectedRole('ASSISTANT');
      fetchMembers();
    } catch (error) {
      console.error('Failed to add member:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke legge til medlem');
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (!window.confirm(`Fjern ${userName} fra denne undersaken?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/subcases/${subcaseId}/members/${userId}`);
      toast.success('Medlem fjernet');
      fetchMembers();
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke fjerne medlem');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Inheritance Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Undersak-medlemmer
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              {inheritMembers ? 'Arver medlemmer fra hovedsak' : 'Egendefinerte medlemmer'}
            </p>
          </div>
          
          {isLead && (
            <button
              onClick={toggleInheritance}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors text-sm font-medium ${
                inheritMembers
                  ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {inheritMembers ? 'Arver fra hovedsak' : 'Egendefinert'}
            </button>
          )}
        </div>

        {/* Info Box */}
        {inheritMembers ? (
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-800">
              <p className="font-medium mb-1">Medlemmer arves automatisk</p>
              <p>Alle medlemmer fra hovedsaken har tilgang til denne undersaken. Klikk på knappen over for å definere egne medlemmer.</p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <Shield className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-gray-700">
              <p className="font-medium mb-1">Egendefinerte medlemmer</p>
              <p>Kun de du legger til her har tilgang til denne undersaken.</p>
            </div>
          </div>
        )}
      </div>

      {/* Members List */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-900">
            Medlemmer ({members.length})
          </h4>
          
          {isLead && !inheritMembers && (
            <button
              onClick={() => {
                fetchAvailableLawyers();
                setShowAddModal(true);
              }}
              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs font-medium"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Legg til
            </button>
          )}
        </div>

        {members.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>Ingen medlemmer</p>
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((member) => (
              <div
                key={member.user_id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-semibold text-indigo-700">
                    {member.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{member.name}</p>
                    <p className="text-xs text-gray-600">{member.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    member.role === 'LEAD' ? 'bg-purple-100 text-purple-700' :
                    'bg-gray-200 text-gray-700'
                  }`}>
                    {member.role}
                  </span>
                  
                  {member.inherited && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                      Arvet
                    </span>
                  )}

                  {isLead && !member.inherited && !inheritMembers && (
                    <button
                      onClick={() => handleRemoveMember(member.user_id, member.name)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Legg til medlem</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Lawyer Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Velg person
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                >
                  <option value="">-- Velg --</option>
                  {availableLawyers.map((lawyer) => (
                    <option key={lawyer.id} value={lawyer.id}>
                      {lawyer.name} ({lawyer.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Velg rolle
                </label>
                <div className="space-y-2">
                  {roles.map((role) => (
                    <label
                      key={role.value}
                      className={`flex items-center p-2 border rounded-lg cursor-pointer transition-colors ${
                        selectedRole === role.value
                          ? 'border-indigo-500 bg-gray-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role.value}
                        checked={selectedRole === role.value}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-900">{role.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Avbryt
              </button>
              <button
                onClick={handleAddMember}
                disabled={!selectedUserId}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Legg til
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcaseMembers;
